/**
 * Base component class from which other components may be derived
 */
import {
  Injector,
  OnDestroy,
  ChangeDetectorRef,
  Type,
  InjectionToken,
  AbstractType,
  Injectable,
  signal,
  Signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { Location } from '@angular/common';

import { E2E_ATTRIBUTES, ZONE_NAME, ZONE_PROPERTY_NAME } from '@shared/types/enums';

import { ZoneHelpers } from '@shared/helpers/zone-helpers/zone-helpers';

import { Observable, BehaviorSubject, Subscription, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { forEach, isEmpty, isFunction } from 'lodash';
import { lazyInject } from '@app/shared/decorators';
import { I18nService } from '@app/shared/services/i18n/i18n.service';
import { I18n } from '@app/i18n/strings-keys';
import { Router } from '@angular/router';
declare const Zone: any;
@Injectable()
export abstract class BaseComponent implements OnDestroy {
  public readonly E2E = E2E_ATTRIBUTES;

  @lazyInject(Location) public location: Location;
  @lazyInject(Router) public router: Router;
  @lazyInject(I18nService) public i18nService: I18nService;

  @lazyInject(ChangeDetectorRef) protected cd: ChangeDetectorRef;

  public readonly isShowLoader: Signal<boolean>;
  public readonly isShowLoader$: Observable<boolean>;
  public readonly dispose$: Observable<number>;
  public forceLocalUpdate = false;
  public subscriptions: Subscription[] = [];

  private cid = Symbol(this.constructor.name);
  private showLoaderSignal = signal<boolean>(false);

  public cancelSubscription$: Subject<void> = new Subject();

  public I18n = I18n;

  private readonly _dispose$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);

  public constructor(protected injector: Injector) {
    this.isShowLoader = this.showLoaderSignal.asReadonly();
    this.isShowLoader$ = toObservable(this.showLoaderSignal);
    this.dispose$ = this._dispose$.pipe(filter((v) => !!v));

    this.decorateComponentMethodsToSaveCallstack();
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  /**
   * Release resources event to prevent memory leaks
   */
  public ngOnDestroy(): void {
    this.cancelSubscription$.next();
    this._dispose$.next(1);
    if (!isEmpty(this.subscriptions)) {
      forEach(this.subscriptions, (subs) => subs?.unsubscribe());
    }
  }

  public afterLanguageChange(): void {
    // To be defined by concrete component
  }

  /**
   * use this method to set the loader state
   * @param value The boolean value indicating whether to show the loader or not.
   */
  public setLoader(value: boolean): void {
    this.showLoaderSignal.set(value);
  }

  private decorateComponentMethodsToSaveCallstack() {
    let descriptors = Object.getOwnPropertyDescriptors(
      Object.getPrototypeOf(this),
    );
    const excludedMethods = ['constructor'];

    for (const [propName, descriptor] of Object.entries(descriptors)) {
      const isMethod =
        typeof descriptor.value === 'function' &&
        !excludedMethods.includes(propName);

      if (isMethod) {
        const originalMethod = descriptor.value;

        descriptor.value = (...args: any[]) => {
          const existingContext = Zone.current.getZoneWith(
            ZONE_PROPERTY_NAME.LOG_ACTION_CONTEXT,
          );

          return ZoneHelpers.runInZone(
            ZONE_NAME.LOG_CONTEXT,
            () => originalMethod.apply(this, args),
            {
              [ZONE_PROPERTY_NAME.CID]: this.cid,
            },
            existingContext &&
              existingContext.get(ZONE_PROPERTY_NAME.CID) !== this.cid
              ? (
                  existingContext.getZoneWith(ZONE_PROPERTY_NAME.LOCKER) ??
                  existingContext
                ).parent
              : null,
          );
        };

        Object.defineProperty(this, propName, descriptor);
      }
    }
  }

  public back(): void {
    this.location.back();
  }
}
